import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import axios from 'axios';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DroneTracking from './pages/DroneTracking';
import ProtectionView3D from './pages/ProtectionView3D';
import Sensors from './pages/Sensors';
import SensorFusion from './pages/SensorFusion';
import Countermeasures from './pages/Countermeasures';
import Intelligence from './pages/Intelligence';
import Timeline from './pages/Timeline';
import Communications from './pages/Communications';
import Monitoring from './pages/Monitoring';
import BehaviorAnalysis from './pages/BehaviorAnalysis';
import Simulation from './pages/Simulation';
import DroneClassification from './pages/DroneClassification';
import MitigationDecision from './pages/MitigationDecision';
import ThreatResponseTimeline from './pages/ThreatResponseTimeline';
import DrillMode from './pages/DrillMode';
import AutoEngage from './pages/AutoEngage';
import LiveMode from './pages/LiveMode';
import EngagementConsole from './pages/EngagementConsole';
import Readiness from './pages/Readiness';
import SaturationDrill from './pages/SaturationDrill';
import SystemCheck from './pages/SystemCheck';
import Layout from './components/Layout';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

let axiosInstance = axios.create({
  baseURL: API
});

export const setAuthToken = (token) => {
  if (token) {
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common['Authorization'];
  }
};

export { axiosInstance as axios };

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    queueMicrotask(() => {
      const token = localStorage.getItem('token');
      if (token) {
        setAuthToken(token);
        setIsAuthenticated(true);
      }
      setLoading(false);
    });
  }, []);

  const handleLogin = (token) => {
    localStorage.setItem('token', token);
    setAuthToken(token);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setAuthToken(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-zinc-950">
        <div className="text-sky-500 font-mono text-sm">INITIALIZING COUNTER-DRONE SYSTEM...</div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
          }
        />
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <Dashboard />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/3d-view"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <ProtectionView3D />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/tracking"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <DroneTracking />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/sensors"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <Sensors />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/fusion"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <SensorFusion />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/countermeasures"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <Countermeasures />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/intelligence"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <Intelligence />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/timeline"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <Timeline />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/behavior"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <BehaviorAnalysis />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/monitoring"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <Monitoring />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/comms"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <Communications />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/simulation"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <Simulation />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/classification"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <DroneClassification />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/mitigation"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <MitigationDecision />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/kill-chain"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <ThreatResponseTimeline />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/drill"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <DrillMode />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/auto-engage"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <AutoEngage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/live"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <LiveMode />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/engagement-console"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <EngagementConsole />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/readiness"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <Readiness />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/saturation"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <SaturationDrill />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/system"
          element={
            isAuthenticated ? (
              <Layout onLogout={handleLogout}>
                <SystemCheck />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;